import { useQuery } from 'data-fetching-client';
import getLang from 'I18n/utils/getLang';
import { useMemo } from 'react';
import { GET_IN_APP_CURRENCIES } from 'pricing-pages-api/currency/queries';
import { CURRENCY } from 'pricing-pages-lib/constants/EarlyRequesterNames';
import { GET_PRODUCTS_QUERY } from '../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.dfc.new';
import { FIND_PURCHASE_OPTIONS_QUERY } from '../../../__generated__/chirp/com/hubspot/purchase/options/rpc/PurchaseOptionsService.dfc.new';

// Base hook that fetches raw POP data for any product
const usePurchaseOptionsData = ({
  productApiName
}) => {
  var _currencyQuery$data, _productDetailsQuery$, _productDetailsQuery$2, _currencyQuery$data2, _purchaseOptionsQuery, _purchaseOptionsQuery2;
  const currencyQuery = useQuery(GET_IN_APP_CURRENCIES, {
    variables: {
      earlyRequestName: CURRENCY
    }
  });
  const productDetailsQuery = useQuery(GET_PRODUCTS_QUERY, {
    variables: {
      productApiNames: [productApiName]
    }
  });
  const purchaseOptionsQuery = useQuery(FIND_PURCHASE_OPTIONS_QUERY, {
    variables: {
      userContext: {
        __typename: 'com.hubspot.purchase.options.models.rpc.context.TouchlessUserContext',
        currencyCode: (_currencyQuery$data = currencyQuery.data) === null || _currencyQuery$data === void 0 ? void 0 : _currencyQuery$data.currencies.defaultCurrencyCode,
        language: getLang().toUpperCase()
      },
      products: !((_productDetailsQuery$ = productDetailsQuery.data) !== null && _productDetailsQuery$ !== void 0 && _productDetailsQuery$.result) ? [] : [{
        skuUuid: productDetailsQuery.data.result.productsByName[productApiName].skuUuid,
        quantity: 1
      }]
    },
    skip: !((_productDetailsQuery$2 = productDetailsQuery.data) !== null && _productDetailsQuery$2 !== void 0 && _productDetailsQuery$2.result) || !((_currencyQuery$data2 = currencyQuery.data) !== null && _currencyQuery$data2 !== void 0 && _currencyQuery$data2.currencies.defaultCurrencyCode)
  });
  return {
    purchaseOptions: ((_purchaseOptionsQuery = purchaseOptionsQuery.data) === null || _purchaseOptionsQuery === void 0 ? void 0 : _purchaseOptionsQuery.result.purchaseOptions) || [],
    currencyCode: ((_purchaseOptionsQuery2 = purchaseOptionsQuery.data) === null || _purchaseOptionsQuery2 === void 0 || (_purchaseOptionsQuery2 = _purchaseOptionsQuery2.result.purchaseOptions) === null || _purchaseOptionsQuery2 === void 0 || (_purchaseOptionsQuery2 = _purchaseOptionsQuery2[0]) === null || _purchaseOptionsQuery2 === void 0 || (_purchaseOptionsQuery2 = _purchaseOptionsQuery2.orderedTerms) === null || _purchaseOptionsQuery2 === void 0 || (_purchaseOptionsQuery2 = _purchaseOptionsQuery2[0]) === null || _purchaseOptionsQuery2 === void 0 || (_purchaseOptionsQuery2 = _purchaseOptionsQuery2.recurringTermDiscountedPrice) === null || _purchaseOptionsQuery2 === void 0 ? void 0 : _purchaseOptionsQuery2.currencyCode) || null,
    loading: currencyQuery.loading || productDetailsQuery.loading || purchaseOptionsQuery.loading,
    error: currencyQuery.error || productDetailsQuery.error || purchaseOptionsQuery.error
  };
};

// Hook that extracts just the first ordered term from each purchase option
const usePurchaseOptionsFirstTerm = ({
  productApiName
}) => {
  const {
    purchaseOptions,
    currencyCode,
    loading,
    error
  } = usePurchaseOptionsData({
    productApiName
  });
  const firstTerms = useMemo(() => {
    return purchaseOptions.map(option => {
      var _option$orderedTerms;
      return (_option$orderedTerms = option.orderedTerms) === null || _option$orderedTerms === void 0 ? void 0 : _option$orderedTerms[0];
    }).filter(Boolean);
  }, [purchaseOptions]);
  return {
    firstTerms,
    currencyCode,
    loading,
    error
  };
};
export const useAnnualMonthlyPricing = ({
  productApiName
}) => {
  const {
    firstTerms,
    currencyCode,
    loading,
    error
  } = usePurchaseOptionsFirstTerm({
    productApiName
  });
  const {
    annualPrice,
    monthlyPrice
  } = useMemo(() => {
    if (!firstTerms || firstTerms.length === 0) {
      return {
        annualPrice: null,
        monthlyPrice: null
      };
    }
    const annualTerm = firstTerms.find(term => term.commitmentTerm === 12);
    const monthlyTerm = firstTerms.find(term => term.commitmentTerm === 1);
    return {
      annualPrice: annualTerm ? Number(annualTerm.recurringTermDiscountedPrice.monthlyPrice) : null,
      monthlyPrice: monthlyTerm ? Number(monthlyTerm.recurringTermDiscountedPrice.monthlyPrice) : null
    };
  }, [firstTerms]);
  return {
    annualPrice,
    monthlyPrice,
    currencyCode,
    loading,
    error
  };
};
export const useProductPromotionIds = ({
  productApiName
}) => {
  const {
    firstTerms,
    loading,
    error
  } = usePurchaseOptionsFirstTerm({
    productApiName
  });
  const promotionIds = useMemo(() => {
    if (!firstTerms || firstTerms.length === 0) {
      return [];
    }
    const recurringPromotionIds = firstTerms.flatMap(term => Object.values(term.recurringPricedProductBySkuUuid || {}).flatMap(skuProduct => {
      var _skuProduct$discounts;
      return ((_skuProduct$discounts = skuProduct.discounts) === null || _skuProduct$discounts === void 0 ? void 0 : _skuProduct$discounts.map(discount => discount.promotionId)) || [];
    }));
    const nonRecurringPromotionIds = firstTerms.flatMap(term => Object.values(term.nonRecurringPricedProductBySkuUuid || {}).flatMap(skuProduct => {
      var _skuProduct$discounts2;
      return ((_skuProduct$discounts2 = skuProduct.discounts) === null || _skuProduct$discounts2 === void 0 ? void 0 : _skuProduct$discounts2.map(discount => discount.promotionId)) || [];
    }));
    return [...recurringPromotionIds, ...nonRecurringPromotionIds];
  }, [firstTerms]);
  return {
    promotionIds,
    loading,
    error
  };
};
export const useBestRecurringTermDiscountedPriceDueUponPurchase = ({
  productApiName
}) => {
  const {
    firstTerms,
    currencyCode,
    loading,
    error
  } = usePurchaseOptionsFirstTerm({
    productApiName
  });
  const bestPrice = useMemo(() => {
    if (!firstTerms || firstTerms.length === 0) {
      return null;
    }
    return getMostAffordablePrice(firstTerms);
  }, [firstTerms]);
  return {
    error,
    loading,
    data: {
      price: bestPrice,
      currencyCode
    }
  };
};
function getMostAffordablePrice(firstTerms) {
  if (firstTerms.length === 0) {
    return null;
  }
  const sortedTerms = [...firstTerms].sort((termA, termB) => {
    const priceA = Number(termA.recurringTermDiscountedPrice.monthlyPrice);
    const priceB = Number(termB.recurringTermDiscountedPrice.monthlyPrice);
    if (priceA < priceB) {
      return -1;
    }
    if (priceA > priceB) {
      return 1;
    }
    return 0;
  });
  return Number(sortedTerms[0].recurringTermDiscountedPrice.monthlyPrice);
}